import { useEffect, useRef, useState } from "react";
import type { ChatMessage, Team } from "@msf/shared";
import { CloseIcon } from "./icons.js";

interface ChatSheetProps {
  messages: ChatMessage[];
  you: Team | null;
  open: boolean;
  onClose: () => void;
  onSend: (text: string) => void;
}

export function ChatSheet({ messages, you, open, onClose, onSend }: ChatSheetProps) {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input as soon as the sheet opens, so you can type + Enter to send.
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  // Keep the log scrolled to the newest message.
  useEffect(() => {
    if (open) requestAnimationFrame(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight));
  }, [open, messages.length]);

  function submit() {
    const t = draft.trim();
    if (!t) return;
    onSend(t);
    setDraft("");
  }

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ${open ? "translate-y-0" : "pointer-events-none translate-y-full"}`}
      aria-hidden={!open}
    >
      <div className="clay mx-auto flex h-[60vh] max-w-[30rem] flex-col rounded-b-none p-3 pad-safe">
        <div className="mb-2 flex items-center">
          <div className="flex-1" />
          <div className="h-1.5 w-10 rounded-full bg-black/15" />
          <div className="flex flex-1 justify-end">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chat"
              tabIndex={open ? 0 : -1}
              className="rounded-full p-1.5 opacity-60"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto pr-1">
          {messages.length === 0 && (
            <p className="mt-8 text-center text-sm opacity-50">Say hi to your opponent</p>
          )}
          {messages.map((m) => {
            const mine = you !== null && m.team === you;
            const accent = m.team === "red" ? "#f2685f" : m.team === "blue" ? "#5aa9e6" : "#93826a";
            return (
              <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <span className="px-1 text-[0.65rem] font-bold" style={{ color: accent }}>
                  {m.nickname}
                </span>
                <span
                  className="max-w-[80%] break-words rounded-2xl px-3 py-1.5 text-sm"
                  style={{ background: mine ? `${accent}33` : "var(--revealed)" }}
                >
                  {m.text}
                </span>
              </div>
            );
          })}
        </div>
        <form
          className="mt-2 flex gap-2 pb-0.5"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={300}
            type="text"
            inputMode="text"
            enterKeyHint="send"
            autoComplete="off"
            autoCapitalize="sentences"
            aria-label="Chat message"
            placeholder="Message…"
            tabIndex={open ? 0 : -1}
            className="clay-inset min-w-0 flex-1 bg-transparent px-3 py-2 text-base outline-none"
          />
          <button
            type="submit"
            tabIndex={open ? 0 : -1}
            className="clay-btn px-4 font-bold"
            disabled={!draft.trim()}
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
